import {computed, Injectable, signal} from '@angular/core';
import {Mailbox, MailboxProvider} from './mailbox.model';

export type MailboxStep = 'INBOUND' | 'OUTBOUND' | 'REVIEW';
export type MailboxMode = 'CREATE' | 'EDIT' | null;

export type MailboxView =
	| 'CREATE_MAILBOX'
	| 'EDIT_MAILBOX'
	| 'CONFIG_TABS'
	| 'CONFIG_WIZARD'
	| 'EMPTY';


@Injectable({ providedIn: 'root' })
export class MailboxConfiguratorStore {
	private readonly _loading = signal(false);
	private readonly _loadingMailboxes = signal(false)
	private readonly _error = signal<string | null>(null);
	private readonly _success = signal<string | null>(null);

	private readonly _step = signal<MailboxStep>('INBOUND');
	private readonly _provider = signal<MailboxProvider | null>(null);
	private readonly _loadedMailbox = signal<Mailbox | null>(null);
	private readonly _mode = signal<MailboxMode>(null);
	private readonly _mailboxes = signal<Mailbox[] | null>(null);
	private _selectedMailboxId = signal<string | null>(null);
	private _view = signal<MailboxView>('EMPTY');

	readonly loading = this._loading.asReadonly();
	readonly loadingMailboxes = this._loadingMailboxes.asReadonly()
	readonly error = this._error.asReadonly();
	readonly success = this._success.asReadonly();

	readonly step = this._step.asReadonly();
	readonly provider = this._provider.asReadonly();
	readonly loadedMailbox = this._loadedMailbox.asReadonly();
	readonly mode = this._mode.asReadonly();
	readonly mailboxes = this._mailboxes.asReadonly();
	readonly selectedMailboxId = this._selectedMailboxId.asReadonly();
	readonly view = this._view.asReadonly();


	private _inbound = signal({
		client: '',
		email: '',
		username: '',
		password: '',
		imap_host: '',
		folder: 'INBOX',
		imap_port: '',
		protocol: '',
		idle_enabled: false,
		ssl_enabled: false,
		start_tls_enabled: false,
	});

	private _outbound = signal({
		client: '',
		email: '',
		username: '',
		password: '',
		alias: '',
		fromName: '',
		email_host: '',
		email_configuration_alias: '',
		from_name: '',
		from_address: '',
		smtp_port: '',
		starttls_enable: '',
		mail_debug: '',
		transport_protocol: '',
		email_auth: '',
		email_mime_charset: '',
	});

	readonly inbound = this._inbound.asReadonly();
	readonly outbound = this._outbound.asReadonly();

	readonly isCustom = computed(() => this.provider() === 'CUSTOM');

	setLoading(val: boolean) {
		this._loading.set(val);
	}

	setLoadingMailboxes(val: boolean) {
		this._loadingMailboxes.set(val);
	}

	setError(err: string | null) {
		this._error.set(err);
	}

	setStep(step: MailboxStep) {
		this._step.set(step);
	}

	setMailboxes(mailboxes: Mailbox[]) {
		this._mailboxes.set(mailboxes);
	}

	setProvider(provider: MailboxProvider) {
		this._provider.set(provider);
	}

	setLoadedMailbox(mailbox: Mailbox | null) {
		this._loadedMailbox.set(mailbox);
	}

	setMode(mode: MailboxMode) {
		this._mode.set(mode);
	}

	setView(view: MailboxView) {
		this._view.set(view);
	}

	patchInbound(data: Partial<ReturnType<typeof this._inbound>>) {
		this._inbound.update(v => ({ ...v, ...data }));
	}

	patchOutbound(data: Partial<ReturnType<typeof this._outbound>>) {
		this._outbound.update(v => ({ ...v, ...data }));
	}

	reset() {
		this._step.set('INBOUND');
		this._provider.set(null);

		this._inbound.set({
			client: '',
			email: '',
			username: '',
			password: '',
			imap_host: '',
			folder: 'INBOX',
			imap_port: '',
			protocol: '',
			idle_enabled: false,
			ssl_enabled: false,
			start_tls_enabled: false,
		});

		this._outbound.set({
			client: '',
			email: '',
			username: '',
			password: '',
			alias: '',
			fromName: '',
			email_host: '',
			email_configuration_alias: '',
			from_name: '',
			from_address: '',
			smtp_port: '',
			starttls_enable: '',
			mail_debug: '',
			transport_protocol: '',
			email_auth: '',
			email_mime_charset: '',
		});
	}
}
